import { Avatar, AvatarFallback, AvatarImage } from '@crowdfpl/ui-web/avatar';
import { Button } from '@crowdfpl/ui-web/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdfpl/ui-web/dropdown-menu';
import { LogOut, User, Trophy, Swords, HelpCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
// import { ThemeToggle } from '@/components/shared/theme/theme-toggle';
import { GamificationFeatureGate } from '@/components/gamification/gamification-feature-gate';
import { TierBadgeButton } from '@/components/gamification/tier-badge-button';
import { NotificationCenter } from '@/components/notifications';
import { resolveOnboardingHeaderSlot } from '@/components/shared/layout/onboarding-header-placement';
import { useAuth } from '@/hooks/auth';
import { useSignOut } from '@/hooks/data/auth';
import { useAppConfig } from '@/hooks/data/config';
import { useOnboarding } from '@/stores/onboarding';

interface MobileHeaderProps {
  onAuthModalOpen: () => void;
}

export const MobileHeader = ({ onAuthModalOpen }: MobileHeaderProps) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const signOut = useSignOut();
  const { show: showOnboarding } = useOnboarding();
  const { data: appConfig, isPending: configPending, isError: configError } = useAppConfig();
  const configReady = !configPending || configError;
  const gamificationEnabled = appConfig?.features.gamification === true;
  const onboardingSlot = resolveOnboardingHeaderSlot(isAuthenticated, gamificationEnabled, configReady);

  const handleLogoClick = (e: React.MouseEvent) => {
    if (window.location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogout = async () => {
    await signOut.mutateAsync();
    navigate('/');
  };

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="logo flex items-center hover:opacity-80 transition-opacity"
            onClick={handleLogoClick}
          >
            <img src="/icons/crowdfpl-icon.svg" alt="CrowdFPL logo" width="28" height="28" />
            <span className="brand-name font-semibold text-lg">CrowdFPL</span>
          </Link>

          {/* User Profile or Sign In */}
          <div className="flex items-center space-x-1">
            {/* <ThemeToggle size="sm" /> */}
            {onboardingSlot === 'header' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={showOnboarding}
                className="h-10 w-10"
                aria-label="How it works"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
            )}
            {onboardingSlot === 'pending' && isAuthenticated && (
              <div className="h-11 w-11 shrink-0" aria-hidden />
            )}
            <GamificationFeatureGate>
              <TierBadgeButton />
            </GamificationFeatureGate>
            <NotificationCenter />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.avatar_url || ''} alt={user?.username || 'User'} />
                      <AvatarFallback className="bg-gradient-to-br from-primary/20 to-competitive-blue/20">
                        {(user?.username || 'U').slice(0, 1).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.username || 'User'}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user?.email || ''}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to={`/profile/${user?.username || 'user'}`} className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/duels" className="flex items-center">
                      <Swords className="mr-2 h-4 w-4" />
                      <span>Duels</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/leaderboard" className="flex items-center">
                      <Trophy className="mr-2 h-4 w-4" />
                      <span>Leaderboard</span>
                    </Link>
                  </DropdownMenuItem>
                  {onboardingSlot === 'menu' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={showOnboarding} className="cursor-pointer">
                        <HelpCircle className="mr-2 h-4 w-4" />
                        <span>How it works</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAuthModalOpen()}
                className="font-medium"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
